/**
 * Custom Cart Automation Script: UTV Bedrail + Equipment Rack Bracket Bundle
 * File Path: assets/gti-cart-promo.js
 * 
 * Instructions:
 * 1. Log in to Shopify Admin -> Online Store -> Themes -> Actions -> Edit Code.
 * 2. Under "Assets", click "Add a new asset". Select "Create an empty file", name it "gti-cart-promo" with a ".js" extension.
 * 3. Copy this entire script and paste it into the file. Save it.
 * 4. Open "layout/theme.liquid" and add this line just before the closing </head> or </body> tag:
 *    <script src="{{ 'gti-cart-promo.js' | asset_url }}" defer></script>
 * 5. This script supports standard Shopify AJAX endpoints. It monitors the cart state and dynamically adds, updates, or removes the required EE082 brackets as items are changed.
 *
 * SCOPE: brackets only. The Piranha PPW200 free-pressure-washer promo that this
 * script also used to deliver was retired on 2026-09-02 (product sold out, no
 * restock) — see shopify-deliverables/PPW200_PROMO_REMOVAL_2026-09-02.md.
 * Do not reintroduce a free-gift add here without reading that doc first.
 */

(function() {
  const promoEl = document.getElementById('gti-promo-config');
  const BRACKET_VARIANT_ID = promoEl ? parseInt(promoEl.dataset.bracketVariantId, 10) : 43449324961881; // Fallback to live ID

  const CONFIG = {
    // Exact handles of our four UTV Bedrail Systems
    UTV_RAIL_HANDLES: [
      'john-deere-gator-utv-bed-rail-system',
      'can-am-defender-hd11-utv-bed-rail-system',
      'club-car-carryall-utv-bedrail-mounting-system',
      'lowes-axis-500-utv-bedrail-mounting-system'
    ],
    // The handle of our EE082 universal bracket product
    BRACKET_HANDLE: 'mounting-bracket',
    BRACKET_VARIANT_ID: BRACKET_VARIANT_ID,
    // Explicit list of trimmer racks that require 2 brackets
    TRIMMER_RACK_HANDLES: [
      '2-place-lockable-trimmer-rack',
      '3-place-lockable-trimmer-rack',
      '4-place-lockable-trimmer-rack',
      '3-place-trimmer-rack-open-trailer',
      '2-place-trimmer-rack-open-trailer'
    ],
    // Explicitly list qualifying commercial equipment racks
    QUALIFYING_RACK_HANDLES: [
      '2-place-lockable-trimmer-rack',
      '3-place-lockable-trimmer-rack',
      '4-place-lockable-trimmer-rack',
      'lockable-backpack-blower-rack-trailer',
      '5-gallon-gas-can-lock-cage',
      '2-5-gallon-gas-can-lock-cage',
      'racing-fuel-can-lockable-cage',
      'lockable-backpack-sprayer-security-cage',
      'storage-rack',
      'lockable-multi-tool-security-cage',
      'trimmer-line-spool-rack-cutter',
      'hand-tool-rack-open-landscape-trailer',
      'backpack-sprayer-trailer-mount-rack',
      '3-place-trimmer-rack-open-trailer',
      '2-place-trimmer-rack-open-trailer',
      'lockable-d-handle-shovel-rack',
      'water-cooler-rack',
      'locking-hedge-trimmer-security-rack',
      'backpack-blower-rack-open-trailer',
      'lockable-multi-tool-rack-trailer',
      'xtreme-pro-mid-mount-hedge-trimmer-support'
    ],
    // Must match the "Free UTV Mounting Brackets" discount's per-order cap in
    // Shopify Admin > Discounts. Orders needing more free brackets than this
    // should be split into a second order — the discount itself only ever
    // covers this many, so exceeding it here would just add a paid line.
    MAX_FREE_BRACKETS_PER_ORDER: 20,
    THROTTLE_MS: 800
  };

  let isUpdating = false;
  let isCartDirty = false;
  let pendingCheckoutRedirect = false;
  let debounceTimeout = null;

  // Evaluates cart items to see what needs adjustment
  function evaluateCartPromotion(forceImmediate = false) {
    if (isUpdating) return;
    // Lock synchronously (not inside the async fetch chain) so a second call
    // fired before this one's /cart.js response lands can't slip past the
    // isUpdating check above and race it into adding a duplicate promo line.
    isUpdating = true;

    if (forceImmediate && debounceTimeout) {
      clearTimeout(debounceTimeout);
      debounceTimeout = null;
    }

    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        let hasUTVRail = false;
        let trimmerRackCount = 0;
        let otherRackCount = 0;
        // Only track promo-managed brackets (tagged by this script), NOT manually added ones.
        let promoBracketQty = 0;
        let promoBracketKey = null;

        cart.items.forEach(item => {
          const isBracket = (item.handle === CONFIG.BRACKET_HANDLE || item.variant_id === CONFIG.BRACKET_VARIANT_ID);
          const isPromoBracket = isBracket && item.properties && item.properties._promo_managed === 'true';

          // 1. Is a UTV Bedrail present?
          if (CONFIG.UTV_RAIL_HANDLES.includes(item.handle)) {
            hasUTVRail = true;
          }

          // 2. Only track promo-managed bracket lines. Manually-added brackets are left alone.
          if (isPromoBracket) {
            promoBracketQty = item.quantity;
            promoBracketKey = item.key;
          }

          // 3. Evaluate other items to see if they are qualifying equipment racks
          const isRack = CONFIG.QUALIFYING_RACK_HANDLES.includes(item.handle);
          if (isRack) {
            if (CONFIG.TRIMMER_RACK_HANDLES.includes(item.handle)) {
              trimmerRackCount += item.quantity;
            } else {
              otherRackCount += item.quantity;
            }
          }
        });

        // Calculate the ideal promo bracket quantity (only for the promo-managed line)
        let targetBracketQty = 0;
        if (hasUTVRail && (trimmerRackCount > 0 || otherRackCount > 0)) {
          targetBracketQty = (trimmerRackCount * 2) + (otherRackCount * 1);
          // Never place more free-tagged brackets than the discount will cover.
          targetBracketQty = Math.min(targetBracketQty, CONFIG.MAX_FREE_BRACKETS_PER_ORDER);
        }

        // Sync only promo-managed items. Manually added brackets are never touched.
        syncCartPromotions(targetBracketQty, promoBracketQty, promoBracketKey);
      })
      .catch(err => {
        console.error('[GTI Promo] Error fetching cart:', err);
        isUpdating = false;
        isCartDirty = false;
        toggleCheckoutButtons(false);
        // Consume the armed redirect here rather than leaving it set — otherwise
        // a later unrelated cart action fires an unrequested jump to /checkout.
        // Honour it immediately though: the customer did click checkout, and a
        // failed promo evaluation must never swallow that click.
        if (pendingCheckoutRedirect) {
          pendingCheckoutRedirect = false;
          window.location.href = '/checkout';
        }
      });
  }

  function toggleCheckoutButtons(disable) {
    const buttons = document.querySelectorAll('form[action="/cart"] [name="checkout"], .cart__checkout-button, .checkout-button, [href="/checkout"], .cart-drawer__footer [name="checkout"]');
    buttons.forEach(btn => {
      if (disable) {
        btn.setAttribute('disabled', 'disabled');
        btn.style.opacity = '0.5';
        btn.style.pointerEvents = 'none';
      } else {
        btn.removeAttribute('disabled');
        btn.style.opacity = '';
        btn.style.pointerEvents = '';
      }
    });
  }

  // Handles syncing of the promo-managed EE082 bracket line.
  // IMPORTANT: Only manages promo-managed line items. Manually added brackets are never touched.
  function syncCartPromotions(targetBracketQty, promoBracketQty, promoBracketKey) {
    const bracketDiff = (targetBracketQty !== promoBracketQty);

    if (!bracketDiff) {
      isUpdating = false;
      isCartDirty = false;
      toggleCheckoutButtons(false);

      // If a checkout redirect was waiting on this sync, proceed to checkout
      if (pendingCheckoutRedirect) {
        pendingCheckoutRedirect = false;
        console.log('[GTI Promo] Cart is in sync. Redirecting to checkout...');
        window.location.href = '/checkout';
      }
      return;
    }

    // isUpdating is already true here — set synchronously by evaluateCartPromotion
    // before this function was ever reached, so it can't race a concurrent call.
    toggleCheckoutButtons(true);

    // If we need to ADD promo brackets for the first time, use /cart/add.js with the _promo_managed tag.
    if (bracketDiff && targetBracketQty > 0 && promoBracketQty === 0) {
      console.log(`[GTI Promo] Adding ${targetBracketQty} promo-managed bracket(s) to cart...`);
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{
            id: CONFIG.BRACKET_VARIANT_ID,
            quantity: targetBracketQty,
            properties: {
              '_promo_managed': 'true'
            }
          }]
        })
      })
      .then(res => {
        // A rejected add (422 "sold out", 404, throttling) still RESOLVES the
        // fetch, so without this check the success path re-enters
        // evaluateCartPromotion, recomputes the same target, and retries the
        // same doomed add forever — with checkout disabled the whole time.
        // That exact loop hit real $1,000+ carts when PPW200 sold out under the
        // old washer branch; don't let the bracket branch repeat it.
        if (!res.ok) {
          throw new Error(`/cart/add.js rejected the bracket line (HTTP ${res.status})`);
        }
        return res.json();
      })
      .then(() => {
        isUpdating = false;
        evaluateCartPromotion(true); // Re-evaluate to confirm sync
      })
      .catch(err => {
        // Bail out and hand the cart back to the customer as-is. The Function
        // discount still prices any EE082 lines that ARE in cart correctly, so
        // the worst case is a missing freebie, never a blocked checkout.
        console.error('[GTI Promo] Failed to add promo brackets:', err);
        isUpdating = false;
        isCartDirty = false;
        toggleCheckoutButtons(false);
        if (pendingCheckoutRedirect) {
          pendingCheckoutRedirect = false;
          window.location.href = '/checkout';
        }
      });
      return;
    }

    // Otherwise, adjust or remove existing promo-managed line items by their key.
    console.log('[GTI Promo] Syncing promo quantities in cart...');
    const updates = {};
    if (bracketDiff && promoBracketKey) {
      // Use the line item key to ONLY update the promo-managed bracket line, not any manually added one.
      updates[promoBracketKey] = targetBracketQty;
    }

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ updates: updates })
    })
    .then(res => {
      if (!res.ok) {
        throw new Error(`/cart/update.js rejected the promo sync (HTTP ${res.status})`);
      }
      return res.json();
    })
    .then(newCart => {
      console.log('[GTI Promo] Sync complete. Refreshing UI.');
      isUpdating = false;
      isCartDirty = false;
      toggleCheckoutButtons(false);
      
      // Fire native event or trigger theme drawer refresh
      dispatchCartUpdateEvent(newCart);

      // If checkout clicked, redirect now
      if (pendingCheckoutRedirect) {
        pendingCheckoutRedirect = false;
        window.location.href = '/checkout';
      }
    })
    .catch(err => {
      // Same reasoning as the add branch: never leave the customer stuck.
      console.error('[GTI Promo] Sync failed:', err);
      isUpdating = false;
      isCartDirty = false;
      toggleCheckoutButtons(false);
      if (pendingCheckoutRedirect) {
        pendingCheckoutRedirect = false;
        window.location.href = '/checkout';
      }
    });
  }

  // Notifies the theme that cart contents changed, handles drawers, or reloads page
  function dispatchCartUpdateEvent(cart) {
    // 1. Dispatch custom events for AJAX drawers
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: cart }));
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true }));

    // 2. Refresh page if on standard cart view or if drawer doesn't auto-update
    if (window.location.pathname === '/cart' || window.location.pathname.includes('/cart')) {
      window.location.reload();
    } else {
      // Update cart count bubbles dynamically in the DOM
      const cartCountElements = document.querySelectorAll('.cart-count, .cart-link__count, .cart-counter, #CartCount, [data-cart-count]');
      if (cartCountElements.length > 0 && cart.item_count !== undefined) {
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
      }

      // Check if theme drawer needs dynamic rendering or soft refresh
      const drawer = document.querySelector('cart-drawer, #CartDrawer, .cart-drawer, .ajax-cart-container, #cart-drawer');
      if (drawer) {
        console.log('[GTI Promo] AJAX Drawer elements detected, skipped page reload.');
        // Shopify standard custom drawer event triggers
        document.dispatchEvent(new Event('cart:refresh', { bubbles: true }));
      } else {
        // Fallback for static headers
        setTimeout(function() { window.location.reload(); }, 200);
      }
    }
  }

  // Debounce helper to prevent multiple rapid triggers during clicks
  function debounce(func, wait) {
    return function(...args) {
      isCartDirty = true; // Mark cart dirty immediately upon change action
      toggleCheckoutButtons(true); // Disable checkout immediately
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  const debouncedEvaluation = debounce(() => evaluateCartPromotion(false), CONFIG.THROTTLE_MS);

  // Monitor DOM modifications or fetch network operations to detect Add-To-Cart actions
  document.addEventListener('DOMContentLoaded', function() {
    evaluateCartPromotion(); // Run immediately on page load

    // Intercept standard form additions or AJAX cart events
    document.addEventListener('cart:added', debouncedEvaluation);
    document.addEventListener('cart:updated', debouncedEvaluation);

    // Listen to click on standard submit/button add forms
    document.addEventListener('click', function(e) {
      const target = e.target;
      if (target && (
        target.matches('[type="submit"][name="add"]') || 
        target.matches('.add-to-cart') || 
        target.matches('.btn--add-to-cart') ||
        target.closest('[type="submit"][name="add"]')
      )) {
        isCartDirty = true;
        toggleCheckoutButtons(true);
        // Wait briefly for the main product to hit the cart, then check promo
        setTimeout(evaluateCartPromotion, CONFIG.THROTTLE_MS);
      }
    });

    // Intercept checkout actions to prevent checking out with out-of-sync promos
    document.addEventListener('click', function(e) {
      const target = e.target;
      const isCheckoutBtn = target && (
        target.matches('form[action="/cart"] [name="checkout"]') ||
        target.matches('.cart__checkout-button') ||
        target.matches('.checkout-button') ||
        target.matches('[href="/checkout"]') ||
        target.closest('form[action="/cart"] [name="checkout"]') ||
        target.closest('.cart__checkout-button') ||
        target.closest('.checkout-button') ||
        target.closest('[href="/checkout"]')
      );

      if (isCheckoutBtn) {
        if (isCartDirty || isUpdating) {
          e.preventDefault();
          e.stopPropagation();
          pendingCheckoutRedirect = true;
          console.log('[GTI Promo] Checkout clicked but cart is dirty. Syncing first...');
          
          if (!isUpdating) {
            evaluateCartPromotion(true); // Force immediate sync
          }
        }
      }
    }, true); // Use capture phase to intercept prior to theme's checkout navigation

    // Intercept native fetch cart calls
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      if (typeof url === 'string' && (
        url.includes('/cart/add') || 
        url.includes('/cart/change') || 
        url.includes('/cart/update') || 
        url.includes('/cart/clear')
      ) && !isUpdating) {
        isCartDirty = true;
        toggleCheckoutButtons(true);
        return originalFetch.apply(this, args).then(res => {
          setTimeout(evaluateCartPromotion, CONFIG.THROTTLE_MS + 200);
          return res;
        });
      }
      return originalFetch.apply(this, args);
    };

    // Intercept native XHR calls (jQuery, Axios, etc.)
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url) {
      this._url = url;
      return originalOpen.apply(this, arguments);
    };

    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function(body) {
      const self = this;
      const onreadystatechange = this.onreadystatechange;
      
      this.onreadystatechange = function() {
        if (self.readyState === 4 && self.status >= 200 && self.status < 300) {
          const url = self._url;
          if (typeof url === 'string' && (
            url.includes('/cart/add') || 
            url.includes('/cart/change') || 
            url.includes('/cart/update') || 
            url.includes('/cart/clear')
          ) && !isUpdating) {
            isCartDirty = true;
            toggleCheckoutButtons(true);
            setTimeout(evaluateCartPromotion, CONFIG.THROTTLE_MS + 200);
          }
        }
        if (onreadystatechange) {
          return onreadystatechange.apply(this, arguments);
        }
      };
      return originalSend.apply(this, arguments);
    };
  });
})();
